import React, { useState } from 'react';
import LocationSearchFieldV2 from '../../../common/LocationSearchFieldV2';

const LocationStep = ({ data, updateData, onNext, onPrevious, isValid, isFirstStep }) => {
  const [error, setError] = useState('');

  // Custom styles for onboarding integration
  const onboardingStyles = `
    .onboarding-location-field .location-input-container {
      position: relative;
    }
    
    .onboarding-location-field .location-input {
      border: none !important;
      border-bottom: 2px solid #d1d5db !important;
      border-radius: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 1rem 2.5rem 1rem 0 !important;
      font-size: 1.25rem !important;
      line-height: 1.75rem !important;
    }
    
    .dark .onboarding-location-field .location-input {
      border-bottom-color: #4b5563 !important;
      color: #ffffff !important;
    }
    
    .onboarding-location-field .location-input:focus {
      border-bottom-color: #8b5cf6 !important;
      transform: scale(1.05) !important;
      box-shadow: 0 10px 25px -5px rgba(139, 92, 246, 0.1) !important;
    }
    
    .dark .onboarding-location-field .location-input:focus {
      border-bottom-color: #a78bfa !important;
    }
    
    .onboarding-location-field .location-input:hover {
      border-bottom-color: #c084fc !important;
    }
    
    .dark .onboarding-location-field .location-input:hover {
      border-bottom-color: #8b5cf6 !important;
    }
    
    .onboarding-location-field .location-input::placeholder {
      color: #9ca3af !important;
    }
    
    .dark .onboarding-location-field .location-input::placeholder {
      color: #6b7280 !important;
    }
    
    .onboarding-location-field .location-clear-btn {
      right: 0 !important;
      top: 50% !important;
      transform: translateY(-50%) !important;
      color: #8b5cf6 !important;
      background: transparent !important;
      border: none !important;
      padding: 0.5rem !important;
    }
    
    .onboarding-location-field .location-loading {
      right: 0 !important;
      border-color: #d1d5db !important;
      border-top-color: #8b5cf6 !important;
    }
    
    .onboarding-location-field .location-loading.with-clear {
      right: 2rem !important;
    }
    
    .onboarding-location-field .location-suggestions-v2 {
      margin-top: 0.5rem !important;
      border-radius: 0.75rem !important;
      box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
      border: 1px solid #e5e7eb !important;
      background: #ffffff !important;
    }
    
    .dark .onboarding-location-field .location-suggestions-v2 {
      background: #374151 !important;
      border-color: #4b5563 !important;
    }
    
    .onboarding-location-field .location-suggestion-item {
      padding: 1rem 1.5rem !important;
      transition: all 0.2s ease-in-out !important;
    }
    
    .onboarding-location-field .location-suggestion-item:hover,
    .onboarding-location-field .location-suggestion-item.focused {
      background-color: #f3f4f6 !important;
      border-left: 3px solid #8b5cf6 !important;
      transform: scale(1.02) !important;
    }
    
    .dark .onboarding-location-field .location-suggestion-item:hover,
    .dark .onboarding-location-field .location-suggestion-item.focused {
      background-color: #4b5563 !important;
    }
    
    .onboarding-location-field .location-suggestion-primary {
      font-size: 1rem !important;
      font-weight: 500 !important;
      color: #111827 !important;
    }
    
    .dark .onboarding-location-field .location-suggestion-primary {
      color: #ffffff !important;
    }
    
    .onboarding-location-field .location-suggestion-secondary {
      font-size: 0.875rem !important;
      color: #6b7280 !important;
    }
    
    .dark .onboarding-location-field .location-suggestion-secondary {
      color: #9ca3af !important;
    }
  `;

  // Add styles to document head
  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = onboardingStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Handle location selection from LocationSearchFieldV2
  const handleLocationSelect = (locationDetails) => {
    console.log('🏠 Onboarding location selected:', locationDetails);
    
    // Format location string for display (city, state, country)
    const locationString = [
      locationDetails.city,
      locationDetails.state,
      locationDetails.country
    ].filter(Boolean).join(', ');
    
    // Convert LocationDetails class instance to plain object for consistency
    const plainLocationData = {
      id: locationDetails.id,
      city: locationDetails.city,
      state: locationDetails.state,
      country: locationDetails.country,
      coordinates: locationDetails.coordinates,
      formattedAddress: locationDetails.formattedAddress,
      timestamp: new Date()
    };
    
    console.log('🏠 Onboarding formatted location string:', locationString);
    console.log('🏠 Onboarding plain location data:', plainLocationData);
    
    // Update form data with structured location object
    updateData('location', locationString);
    updateData('locationData', plainLocationData);
    
    setError('');
  };

  // Handle location input change
  const handleLocationChange = (value) => {
    // Update basic location data immediately for validation
    updateData('location', value);
    setError('');
  };



  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (isValid) {
      onNext();
    }
  };

  return (
    <div className="space-y-8 md:space-y-12">
      <form onSubmit={handleSubmit} className="space-y-8 md:space-y-12">
        {/* Question */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white leading-tight">
            Where are you based?
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400">
            Find Burnt members near you!
          </p>
        </div>

        {/* Location Search Field */}
        <div className="space-y-6">
          <div className="relative">
            <LocationSearchFieldV2
              value={data.location || ''}
              onChange={handleLocationChange}
              onLocationSelect={handleLocationSelect}
              placeholder="Start typing your city or location..."
              className="onboarding-location-field"
              inputClassName="w-full px-6 py-4 text-xl bg-transparent border-0 border-b-2 border-gray-300 dark:border-gray-600 
                           focus:outline-none focus:border-purple-500 dark:focus:border-purple-400
                           dark:text-white dark:placeholder-gray-500 placeholder-gray-400
                           transition-all duration-300 ease-in-out
                           hover:border-purple-300 dark:hover:border-purple-500
                           focus:scale-105 focus:shadow-lg focus:shadow-purple-500/10"
              required
              debounceMs={150}
              minQueryLength={2}
            />
            
            {/* Animated underline */}
            <div className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300 ${
              data.location ? 'w-full opacity-100' : 'w-0 opacity-0'
            }`}></div>
          </div>



          {/* Error message */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400 text-center font-medium">{error}</p>
            </div>
          )}


        </div>

        {/* Enhanced Navigation Buttons */}
        <div className="flex justify-between items-center border-none !border-0">
          {!isFirstStep && (
            <button
              type="button"
              onClick={onPrevious}
              className="px-6 py-3 rounded-lg text-lg font-medium text-gray-600 dark:text-gray-400 
                       hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 
                       dark:hover:bg-gray-700 transition-all duration-300 ease-in-out
                       transform hover:scale-105 active:scale-95 focus:ring-4 focus:ring-gray-500/20"
            >
              Back
            </button>
          )}
          
          <button
            type="submit"
            disabled={!isValid}
            className={`px-8 py-4 rounded-lg text-lg font-medium transition-all duration-300 ease-in-out transform ${
              isValid
                ? 'bg-purple-600 hover:bg-purple-700 text-white shadow-lg hover:shadow-xl hover:shadow-purple-500/25 hover:scale-105 active:scale-95 focus:ring-4 focus:ring-purple-500/20'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed opacity-60'
            } ${isFirstStep ? 'ml-auto' : ''}`}
          >
            <span className="flex items-center">
              Continue
              <span className={`ml-2 transition-transform duration-300 ${isValid ? 'translate-x-0' : '-translate-x-1'}`}>
                →
              </span>
            </span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default LocationStep; 